import { DownCaratIcon } from 'foundations-assets/icons/components/DownCaratIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { Button } from 'foundations-components/transitional/components/Button';
import styled from 'foundations-theming/utils';
import { useLocation } from 'quartz-routing/react-router';
import RouterSideNav from 'ui-addon-react-router-dom/RouterSideNav';
import RouterSideNavItem from 'ui-addon-react-router-dom/RouterSideNavItem';
import { useCallback, useEffect, useRef, useState } from 'react';
import { breakpointValues } from '../../../constants/Breakpoints';

/*
 * The single, responsive sidenav.
 *
 * WHY CSS-only (no useIsMobile / JS breakpoint branch): public pages are
 * pre-rendered and cached by Botify. The crawler renders at one viewport, so a
 * JS `isMobile` branch bakes that single layout into the cached HTML and serves
 * it to every viewport (e.g. desktop users getting the mobile nav). Choosing the
 * layout with CSS media queries keeps the cached markup viewport-agnostic — each
 * browser applies the layout for its own width. Mirrors the merch-card fix
 * (commit "CSS-only mobile layouts to fix Botify desktop caching").
 *
 * The nav items, footer, and toggle are each rendered exactly ONCE; a CSS grid
 * reflows them between layouts via `grid-template-areas`. Rendering once (rather
 * than a desktop copy + a mobile copy) avoids duplicate `data-test-id`s,
 * duplicate links, and a duplicate currency picker.
 *
 * `isOpen` is mobile disclosure state only. The list-collapse rules live solely
 * inside the mobile media query, so on desktop the list is always shown and the
 * toggle state never affects the desktop (or cached) layout.
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MOBILE_EM = (breakpointValues.tablet - 1) / 16;
const MOBILE = `@media (max-width: ${MOBILE_EM}em)`;
const DESKTOP_TOP = '24px';
// Matches the foundations `boxShadow="200"` token used by the prior mobile bar.
const BAR_SHADOW = '0 2px 4px rgba(0, 0, 0, 0.1)';
const useGlobalNavHeight = ({
  enabled
}) => {
  const [height, setHeight] = useState(0);
  const measure = useCallback(() => {
    if (!enabled) return;
    const globalNav = document.querySelector('.hs-global-nav-header');
    if (globalNav) setHeight(globalNav.getBoundingClientRect().height);
  }, [enabled]);
  useEffect(() => {
    measure();
    const observer = new MutationObserver(measure);
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    return () => observer.disconnect();
  }, [measure]);
  return height;
};

/*
 * Grid container. Desktop: a vertical sidebar stacking the list over the footer.
 * Mobile: a sticky top bar holding the toggle + footer, with the list dropping
 * below as an overlay. Same children, reflowed via grid-template-areas.
 *
 * `position` comes through the transient `$position` prop (not an inline style)
 * so it lives at CSS-rule level: the desktop sidebar keeps its sticky/fixed
 * positioning in the cached HTML, and the mobile media query overrides it to a
 * sticky bar without needing `!important`.
 */
const NavRoot = styled.nav.withConfig({
  displayName: "SideNav__NavRoot"
})(({
  theme,
  $position
}) => ({
  position: $position,
  insetBlockStart: DESKTOP_TOP,
  display: 'grid',
  gridTemplateAreas: '"list" "footer"',
  // auto auto: NavRoot is the scroll container so 1fr has no base height to
  // distribute — minmax(0,1fr) would collapse the list row to 0 and the
  // footer would overlap it.
  gridTemplateRows: 'auto auto',
  /*
   * The whole nav (items + footer/currency picker) is the scroll unit.
   * overflowX:hidden prevents a spurious horizontal scrollbar caused by
   * nav items that bleed 12px left for their active indicator.
   */
  overflowY: 'auto',
  overflowX: 'hidden',
  // 24px = DESKTOP_TOP sticky offset; 16px = bottom clearance.
  // Previously this value lived on ListArea (which starts ~40px lower), so
  // 96px was correct there. Now it's on NavRoot itself, so 40px is right.
  maxBlockSize: `calc(100dvh - ${DESKTOP_TOP} - 16px)`,
  marginInlineEnd: theme.space[900],
  marginBlockEnd: theme.space[1300],
  paddingInlineStart: theme.space[200],
  [MOBILE]: {
    position: 'sticky',
    insetBlockStart: 'var(--mobile-top, 0px)',
    gridTemplateAreas: '"toggle footer" "list list"',
    gridTemplateColumns: '1fr auto',
    gridTemplateRows: 'auto auto',
    alignItems: 'center',
    overflowY: 'unset',
    overflowX: 'unset',
    maxBlockSize: 'none',
    marginInlineEnd: 0,
    marginBlockEnd: 0,
    paddingInlineStart: 0,
    marginInline: `-${theme.space[200]}`,
    paddingInline: theme.space[500],
    paddingBlock: theme.space[100],
    backgroundColor: theme.color['fill-surface-default'],
    boxShadow: BAR_SHADOW,
    zIndex: 50
  }
}));

/*
 * Mobile-only disclosure toggle. Hidden on desktop where the list is always
 * visible (the collapse rules are scoped to the mobile media query).
 */
const ToggleArea = styled.div.withConfig({
  displayName: "SideNav__ToggleArea"
})({
  display: 'none',
  [MOBILE]: {
    gridArea: 'toggle',
    display: 'block',
    minInlineSize: 0
  }
});
const FooterArea = styled.div.withConfig({
  displayName: "SideNav__FooterArea"
})({
  gridArea: 'footer',
  [MOBILE]: {
    justifySelf: 'end',
    flexShrink: 0,
    maxInlineSize: '160px'
  }
});

/*
 * The list. Desktop: a scrollable sidebar column. Mobile: collapsed by default,
 * expanding to a fixed overlay below the sticky bar when `data-open` is true.
 * `--overlay-top` is the measured bottom of the bar so the overlay opens flush
 * beneath it. The `id="scroll-container"` is retained for scroll styling/tests.
 */
const ListArea = styled.div.withConfig({
  displayName: "SideNav__ListArea"
})(({
  theme
}) => ({
  gridArea: 'list',
  [MOBILE]: {
    maxBlockSize: 'none',
    '&[data-open="false"]': {
      display: 'none'
    },
    '&[data-open="true"]': {
      position: 'fixed',
      insetBlockStart: 'var(--overlay-top, 0px)',
      insetBlockEnd: 0,
      insetInlineStart: 0,
      insetInlineEnd: 0,
      zIndex: 49,
      overflowY: 'auto',
      backgroundColor: theme.color['fill-surface-default'],
      paddingInline: theme.space[300],
      paddingBlockStart: theme.space[300],
      paddingBlockEnd: theme.space[600]
    }
  }
}));
function SideNav({
  selectedNavItem,
  selectedItemLabel,
  menuAriaLabel,
  sections,
  footerSlot,
  desktopPosition = 'sticky',
  trackGlobalNavHeight = false
}) {
  const [isOpen, setIsOpen] = useState(false);
  const navRootRef = useRef(null);
  const {
    pathname
  } = useLocation();
  const globalNavHeight = useGlobalNavHeight({
    enabled: trackGlobalNavHeight
  });

  // Close the mobile menu on navigation.
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  // Escape + click-outside dismissal while the mobile menu is open.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = e => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    const handleClickOutside = e => {
      const target = e.target;
      if (navRootRef.current && !navRootRef.current.contains(target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isOpen]);

  // Lock body scroll behind the open mobile overlay.
  // overflow:hidden alone does not stop touch-scroll on iOS Safari; fixing
  // the body (with top = -scrollY so content doesn't jump) is the only
  // reliable cross-platform approach.
  useEffect(() => {
    if (!isOpen) return;
    const scrollY = window.scrollY;
    const pathnameAtOpen = window.location.pathname;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      // Skip restoration on route transitions — new page should start at top.
      if (window.location.pathname === pathnameAtOpen) {
        window.scrollTo(0, scrollY);
      }
    };
  }, [isOpen]);

  // Measure the sticky bar so the mobile overlay opens flush beneath it.
  const [overlayTop, setOverlayTop] = useState(0);
  const measureOverlayTop = useCallback(() => {
    if (navRootRef.current) {
      setOverlayTop(navRootRef.current.getBoundingClientRect().bottom);
    }
  }, []);
  useEffect(() => {
    if (!navRootRef.current) return;
    const observer = new ResizeObserver(measureOverlayTop);
    observer.observe(navRootRef.current);
    return () => observer.disconnect();
  }, [measureOverlayTop]);
  useEffect(() => {
    if (isOpen) measureOverlayTop();
  }, [isOpen, measureOverlayTop]);
  const navItems = sections.map((sectionConfig, index) => /*#__PURE__*/_jsx(RouterSideNav, {
    divider: index !== 0,
    headingText: sectionConfig.headingText,
    children: sectionConfig.items.map(item => /*#__PURE__*/_jsx(RouterSideNavItem, {
      "data-test-id": `side-nav-${item.navItem}`,
      "data-is-active": selectedNavItem === item.navItem,
      "data-selenium-class": "side-nav",
      active: selectedNavItem === item.navItem,
      title: /*#__PURE__*/_jsx(Text, {
        textWrap: "nowrap",
        children: item.title
      }),
      to: item.to,
      padding: "400",
      marginInlineStart: "300",
      onMouseEnter: item.onMouseEnter,
      onMouseLeave: item.onMouseLeave
    }, item.navItem))
  }, sectionConfig.section));

  // Runtime measurements ride along as CSS custom properties consumed by the
  // styles: the mobile sticky-bar offset and the open-overlay start position.
  const rootStyle = {
    ['--mobile-top']: `${globalNavHeight}px`,
    ['--overlay-top']: `${overlayTop}px`
  };
  return /*#__PURE__*/_jsxs(NavRoot, {
    ref: navRootRef,
    "aria-label": menuAriaLabel,
    $position: desktopPosition,
    style: rootStyle,
    children: [/*#__PURE__*/_jsx(ToggleArea, {
      children: /*#__PURE__*/_jsx(Button, {
        use: "transparent",
        "aria-expanded": isOpen,
        "aria-controls": "scroll-container",
        onClick: () => setIsOpen(prev => !prev),
        "data-test-id": "side-nav-mobile-toggle",
        paddingInlineStart: "0",
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          gap: "200",
          children: [/*#__PURE__*/_jsx(Text, {
            typography: "heading-200",
            children: selectedItemLabel
          }), /*#__PURE__*/_jsx(DownCaratIcon, {
            size: "100"
          })]
        })
      })
    }), footerSlot && /*#__PURE__*/_jsx(FooterArea, {
      children: footerSlot
    }), /*#__PURE__*/_jsx(ListArea, {
      id: "scroll-container",
      "data-open": isOpen,
      role: "region",
      "aria-label": menuAriaLabel,
      children: navItems
    })]
  });
}
export default SideNav;